import { ReactNode, useId, cloneElement, isValidElement, Children } from "react";

interface FormFieldProps {
  label: string;
  children: ReactNode;
  required?: boolean;
  error?: string;
  hint?: string;
}

export const FormField = ({
  label,
  children,
  required = false,
  error,
  hint,
}: FormFieldProps) => {
  const generatedId = useId();
  const fieldId = `field-${generatedId}`;
  const errorId = error ? `error-${generatedId}` : undefined;
  const hintId = hint ? `hint-${generatedId}` : undefined;

  // Build aria-describedby from available ids
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  // Clone child element to add accessibility attributes
  const enhancedChildren = Children.map(children, (child) => {
    if (isValidElement(child)) {
      return cloneElement(child as React.ReactElement<any>, {
        id: fieldId,
        "aria-required": required || undefined,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy,
      });
    }
    return child;
  });

  return (
    <div className="space-y-2">
      <label
        htmlFor={fieldId}
        className="block text-sm font-medium text-text-primary"
      >
        {label}
        {required && (
          <span className="text-error ml-1" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only">(required)</span>}
      </label>
      {enhancedChildren}
      {hint && !error && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs text-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
